// translations/pages/landing/en.js

export const landingPageEN = {
  "hero": {
    "title": "pAIoneer Labs GmbH",
    "subtitle": "Local AI solutions with data protection in focus",
    "vision": "The next stage of productivity: Organisation as a Service. AI staff take work from the idea to the verified result.",
    "intro": "We research, develop and integrate technologies in artificial intelligence, machine learning and data processing. The result is our own products and customer-specific solutions that analyse, structure, anonymise and protect data within local or customer-owned IT infrastructure — as well as, with [Veridion](veridion), fully automated AI employees that work together with people to take on entire areas of company work.",
    "detail": "Software we install for a customer runs in the customer's own infrastructure and needs no cloud connection. Documents, data and language models stay in-house."
  },
  "partners": {
    "title": "Partners who trust us",
    "subtitle": "Companies and research institutions where our AI organisation is in productive operation.",
    "items": {
      "bisigma": {
        "name": "BiSigma",
        "tagline": "BioSignal Measurement and Analysis\nEye-Tracking and Biosignal Solutions for Your Research",
        "description": [
          "BiSigma specialises in systems for measuring and analysing biosignals as well as high-precision eye-tracking solutions for science, clinical practice and industrial research.",
          "pAIoneer Labs GmbH has completely replaced software development at BiSigma with AI, which successfully develops and continuously advances the software for the measurement and analysis of biosignals."
        ],
        "cta": "Visit BiSigma"
      }
    }
  },
  "products": {
    "title": "Products",
    "subtitle": "We sell software as desktop applications for the workplace, as server software on-premises or in the customer's cloud, and as subscriptions on our platform, such as the [trading assistant](finance), which our AI staff develop themselves. We also build AI systems to customer requirements and provide consulting and training.",
    "partnerLabel": "Partner product",
    "priceOnRequest": "Price on request",
    "request": "Send an enquiry",
    "items": {
      "kyveil": {
        "name": "Kyveil Desktop",
        "description": [
          "Kyveil detects and anonymizes personal data in Word, Excel, OpenDocument, PDF and text files before they go into AI tools, tests, analyses or to third parties. Detection combines {presidio}, {spacy} and {flair} and works in German, English, French, Italian and Spanish.",
          "The desktop version runs on its own on standard computers with Windows, macOS or Linux, without a GPU and without network dependency; the files never leave the workstation. The API version runs in the customer's infrastructure, on-premises or in the customer's own cloud, and builds anonymization into existing systems and workflows.",
          "Replace mode substitutes personal data with placeholders for compliance documentation, fake mode with plausible substitute values for test environments; the document structure is preserved. With AnonChat, an anonymized document reaches ChatGPT, Claude, Gemini or an internal language model only after a preview."
        ],
        "cta": "View Kyveil"
      },
      "veridion": {
        "name": "Veridion Agentic Company Intelligence",
        "claim": "One creator, infinitely many solutions.",
        "text": [
          "The company AI of pAIoneer Labs GmbH: it steers the entire company, as a complete organisation of AI staff with business units from software development through electronics and quantum computing to sales and administration, led by a coordination layer of its own.",
          "This workforce carries projects, bookkeeping, company knowledge, research, development and data analysis to the end on its own. Its communication is optimised by game theory, and when it needs a capability that does not exist yet, it builds its own AI system for it.",
          "It runs on premises, in your own cloud or on our platform. There it develops the [trading assistant](finance) itself and is the AI in its chat."
        ],
        "points": [
          "Integrates into every process",
          "Learns on its own",
          "Creates missing capabilities itself",
          "Plannable in fixed cycles"
        ],
        "cta": "View Veridion"
      },
      "robuddy": {
        "name": "RoBuddy",
        "description": [
          "RoBuddy is an AI-based, modular agent system for companies that automates administrative office tasks.",
          "Five roles cover the working day: the assistant coordinates appointments, drafts e-mails and handles daily tasks. The coach supports the emotional and social side of working life, helps with conflicts and supports personal development. The data protection helper answers specialist questions on data protection and privacy and can be trained on further fields such as fire safety or compliance. The training coach creates training plans, onboardings and course modules, and the advisor analyses uploaded documents, supports decisions and gives strategic recommendations.",
          "RoBuddy is hosted and managed entirely to German standards (GDPR, BSI IT-Grundschutz) and connects seamlessly to Nextcloud or any network drive.",
          "RoBuddy is the agentic system for office work and can be connected with Veridion into one company AI that covers the whole company: RoBuddy takes over the tasks at the workplace, Veridion connects the company's systems and data."
        ],
        "cta": "View RoBuddy",
        "vendorNote": "RoBuddy is a product of Dr. Hornecker Software-Entwicklung und IT-Dienstleistungen. It is distributed by pAIoneer Labs GmbH."
      }
    }
  },
  "platformNote": {
    "title": "SaaS platform",
    "description": "You use this application by subscription directly in the browser, with nothing to install. It runs on our own platform, sign-in requires a second factor, and all AI calls go through one central access point with fixed quotas.",
    "apps": {
      "finance": {
        "name": "AI trading assistant",
        "text": "You connect your brokerage account, ask questions about your positions in plain language and see which news moved the prices. For every financial instrument, a separate machine-learning model, retrained daily, calculates the forecast, and an automation can trade the signals. The assistant is developed by our AI staff; in the chat they are also its AI.",
        "cta": "View the trading assistant"
      }
    }
  },
  "services": {
    "title": "Core services",
    "veridion": {
      "title": "AI staff",
      "description": "Whole fields of work, staffed with AI employees that integrate into the company's processes and tools and carry work to the end on their own. This is how we work ourselves: pAIoneer Labs GmbH runs on it.",
      "cta": "View Veridion",
      "features": [
        "Organisation as a Service",
        "Business units of AI staff",
        "Game-theory-optimised communication",
        "On premises or your own cloud"
      ]
    },
    "custom": {
      "title": "Customer-specific AI systems",
      "description": "We develop systems for analysing, processing, structuring, anonymising and protecting data to customer requirements and integrate them into local or customer-owned IT infrastructures.",
      "features": [
        "Operation without cloud",
        "Product engineering",
        "Prototypes",
        "Research and development projects"
      ]
    },
    "dataanalysis": {
      "title": "Conversational data analysis",
      "description": "An AI employee answers questions put to connected data sources. It reads the catalogs, schemas and columns of the databases, presents an analysis plan before accessing data and clarifies ambiguities.",
      "features": [
        "SQL data sources",
        "Correlation and regression",
        "Tables and charts",
        "PDF and PowerPoint export"
      ]
    },
    "memory": {
      "title": "Company memory",
      "description": "A memory instead of a document search: facts, decisions, tasks and procedures, linked to the projects, people, systems and documents they belong to, with the time in which they hold. It grows on its own from conversations, meetings and work results; a newer decision supersedes the older one on the same topic, nothing is lost.",
      "features": [
        "Knowledge graph",
        "Facts with their time",
        "Decisions, never lost",
        "Access by responsibility"
      ]
    },
    "elektronik": {
      "title": "Electronics & firmware",
      "description": "Hardware-near development as a service: circuit design and simulation as a digital twin, firmware down to the sensor, judgement of components and alternatives before anything is built.",
      "features": [
        "Digital twins",
        "Embedded firmware",
        "Datasheet analysis",
        "Component judgement"
      ]
    },
    "quantum": {
          "title": "Sovereign LLMs & Quantum AI",
          "description": "Our goal under Veridion: developing compact language models Made in Germany that will compete with large models from the US and China, alongside our own Quantum Transformers.",
          "cta": "View Quantum",
          "features": [
                "LLMs Made in Germany",
                "Quantum Transformers",
                "Own Quantum IDE",
                "AI Develops AI"
          ]
    },
    "kyveil": {
      "title": "Local No-GPU Anonymization",
      "description": "Kyveil anonymizes personal data locally, as a desktop application on standard computers with Windows, macOS or Linux or as a REST API in the customer's infrastructure, without cloud dependency and without a GPU.",
      "cta": "View Kyveil",
      "features": [
        "Anonymization before AI",
        "NLP PII detection",
        "Windows, macOS, Linux",
        "REST API on-premises"
      ]
    },
    "agenticdev": {
      "title": "Intent-Driven Development",
      "description": "AI-assisted software development needs reliable context. IDD records goals, non-goals, scenarios, agent rules and decisions as usable project memory.",
      "cta": "View IDD",
      "features": [
        "Agentic development",
        "IDD vault",
        "Intent/Decisions/Scenarios",
        "Scenarios as tests"
      ]
    },
    "finance": {
      "title": "Finance & market analysis",
      "description": "Behind the trading assistant stands a business unit of its own, made of digital staff: AI data scientists and finance experts. They evaluate news in real time, analyse the emotions of financial news and compute on the market data themselves in the chat: correlations, regressions, charts and the track record of their own forecasts, every figure from an executed calculation step. Underneath work a machine-learning forecast per financial instrument and automated paper and live trading.",
      "cta": "View finance",
      "features": [
        "AI data scientists & finance experts",
        "News in real time, with emotion analysis",
        "ML forecast per financial instrument",
        "Automated trading"
      ]
    },
    "consulting": {
      "title": "Consulting",
      "description": "We advise on the technical, organisational and strategic side of introducing AI, securing it and using it in compliance with the rules.",
      "features": [
        "AI governance",
        "Data management",
        "Data protection",
        "Information security"
      ]
    }
  },
  "contact": {
    "title": "Contact",
    "addressLabel": "Address",
    "phoneLabel": "Phone",
    "emailLabel": "Email",
    "cta": "Write a message"
  }
};
